import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Building2, PenTool, Lamp, Wine, ArrowRight } from 'lucide-react';
import { setFavicon } from '../lib/favicon';
import { setCanonical } from '../lib/seo';

const directions = [
  { label: 'GRAĐEVINSTVO', to: '/gradnja', img: '/grup/gradnja.webp', Icon: Building2, testid: 'grup-card-gradnja' },
  { label: 'DIZAJN AGENCIJA', to: '/dizajn', img: '/grup/dizajn-v4.webp', Icon: PenTool, testid: 'grup-card-dizajn' },
  { label: 'DIZAJNERSKE SVJETILJKE', to: '/svjetiljke', img: '/grup/svjetiljke.webp', Icon: Lamp, testid: 'grup-card-svjetiljke', soon: true },
  { label: 'VINO', to: '/vino', img: '/grup/vino.webp', Icon: Wine, testid: 'grup-card-vino', soon: true },
];

export default function GrupLanding() {
  useEffect(() => {
    document.title = 'Nova Stron Grup | Različiti svjetovi. Jedna vizija.';
    setFavicon('/grup/favicon-grup-white.png');
    setCanonical('https://nova-stron.eu/');
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="grup-landing" className="min-h-screen bg-[#f4f2ee] text-[#1c1c1c] flex flex-col">
      {/* Header */}
      <header className="flex items-start justify-between px-6 sm:px-10 lg:px-16 pt-8 lg:pt-10">
        <motion.img
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          src="/grup/logo.png"
          alt="Nova Stron Grup"
          data-testid="grup-logo"
          className="h-16 sm:h-20 lg:h-24 w-auto"
        />
        <span className="text-xs tracking-[0.3em] text-[#1c1c1c]/60 mt-2">HR</span>
      </header>

      {/* Headline */}
      <div className="px-6 sm:px-10 lg:px-16 mt-12 lg:mt-20">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4">
          <motion.h1
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-light uppercase leading-[1.15] tracking-[0.14em] text-3xl sm:text-4xl lg:text-5xl"
          >
            Različiti svjetovi.
          </motion.h1>
          <motion.span
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="font-light uppercase tracking-[0.3em] text-lg sm:text-xl lg:text-2xl text-[#cab28e]"
          >
            Jedna vizija.
          </motion.span>
        </div>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="h-px bg-[#1c1c1c]/20 mt-6 origin-left"
        />
      </div>

      {/* Direction Cards */}
      <div className="px-6 sm:px-10 lg:px-16 mt-10 lg:mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {directions.map(({ label, to, img, Icon, testid, soon }, i) => {
          const delay = 0.35 + i * 0.18;
          const ease = [0.77, 0, 0.175, 1];
          return (
            <motion.div
              key={to}
              initial={{ clipPath: 'inset(100% 0% 0% 0%)', y: 40 }}
              animate={{ clipPath: 'inset(0% 0% 0% 0%)', y: 0 }}
              transition={{ duration: 1.1, delay, ease }}
              className="rounded-sm overflow-hidden"
            >
              <Link
                to={to}
                data-testid={testid}
                className="group relative block overflow-hidden rounded-sm aspect-[2/3] lg:aspect-[9/16]"
              >
                <motion.div
                  initial={{ scale: 1.4 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.6, delay, ease }}
                  className="absolute inset-0"
                >
                  <img
                    src={img}
                    alt={label}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={soon ? { filter: 'saturate(0.6) contrast(0.9) brightness(1.05) blur(10px)', transform: 'scale(1.06)' } : undefined}
                  />
                </motion.div>
                {soon && <div className="absolute inset-0 bg-[#f4f2ee]/15 mix-blend-soft-light" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                {soon && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: delay + 0.7 }}
                    data-testid={`${testid}-soon-badge`}
                    className="absolute top-3 right-3 lg:top-4 lg:right-4 px-3 py-1.5 rounded-full bg-black/45 backdrop-blur-md border border-white/25 text-white text-[9px] sm:text-[10px] tracking-[0.3em] uppercase"
                  >
                    Uskoro
                  </motion.span>
                )}
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: delay + 0.55, ease: 'easeOut' }}
                  className="absolute bottom-0 left-0 right-0 flex flex-col items-center pb-6 lg:pb-8 px-3 text-white"
                >
                  <Icon className="w-6 h-6 mb-3 opacity-90" strokeWidth={1.2} />
                  <span className="text-[11px] sm:text-xs lg:text-sm font-light tracking-[0.25em] text-center uppercase">
                    {label}
                  </span>
                  <ArrowRight className="w-4 h-4 mt-3 opacity-80 transition-transform duration-300 group-hover:translate-x-2" strokeWidth={1.2} />
                </motion.div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Footer */}
      <footer className="px-6 sm:px-10 lg:px-16 py-10 lg:py-12 mt-auto flex items-end justify-between text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#1c1c1c]/60">
        <div>
          <div className="w-8 h-px bg-[#1c1c1c]/40 mb-3" />
          Vrijednosti<br />koje traju.
        </div>
        <div className="text-right">
          Nova Stron Grup
        </div>
      </footer>
    </div>
  );
}
